import React from 'react'
import {
  Box,
  Text,
  Color,
  FontWeight,
  Display,
  FlexDirection,
  JustifyContent,
  Divider,
  AlignItems,
  Icon,
  IconVariant,
} from '@gousto-internal/citrus-react'
import type { Recipe } from '@library/menu-service'
import { FlexOrderSummary } from './FlexOrderSummary'
import { InfoMessage } from './InfoMessage'
import { ValueBanner } from './ValueBanner'
import {
  formatPrice,
  getDeliveryTotal,
  getDiscount,
  getProductTotal,
  getSurchargeTotal,
} from './orderSummaryPricing'
import type { DeliveryPricing } from './types'

type OrderSummaryProps = {
  basketRecipes: {
    recipe: Recipe
    quantity: number
  }[]
  deliveryPricing: DeliveryPricing
  isValueBannerEnabled?: boolean
  shouldShowOrderSummary: boolean
  numberOfRecipes: number
}

export const OrderSummary = ({
  basketRecipes,
  deliveryPricing,
  isValueBannerEnabled = false,
  numberOfRecipes,
  shouldShowOrderSummary,
}: OrderSummaryProps) => {
  const discount = getDiscount(deliveryPricing)
  const productTotal = getProductTotal(deliveryPricing)
  const surchargeTotal = getSurchargeTotal(deliveryPricing)
  const recipeTotal = formatPrice(deliveryPricing.recipeTotal)
  const deliveryTotal = getDeliveryTotal(deliveryPricing)
  const finalTotal = formatPrice(deliveryPricing.total)
  const originalTotal = deliveryPricing.grossTotal ? formatPrice(deliveryPricing.grossTotal) : null

  return (
    <Box
      bg={Color.White}
      borderRadius={1.5}
      boxShadow="0 1px 6px rgba(0, 0, 0, 0.12)"
      paddingH={4}
      paddingV={4}
      color={Color.ColdGrey_900}
      style={{ borderRadius: '6px' }}
    >
      <Box display={Display.Flex} alignItems={AlignItems.Center} gap={2}>
        <Icon name="payment" variant={IconVariant.Default} size={5} />
        <Text fontWeight={FontWeight.Bold}>Order total</Text>
      </Box>

      {shouldShowOrderSummary ? (
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={3}
          style={{ paddingTop: '0.5rem' }}
        >
          <FlexOrderSummary
            basketRecipes={basketRecipes}
            deliveryPricing={deliveryPricing}
            numberOfRecipes={numberOfRecipes}
            recipeTotal={recipeTotal}
          />

          {discount && (
            <Box
              display={Display.Flex}
              justifyContent={JustifyContent.SpaceBetween}
              bg={Color.Success_50}
              borderRadius={1.5}
              paddingH={3}
              paddingV={2}
              data-testid="order-summary-discount-row"
              style={{ borderRadius: '6px' }}
            >
              <Text color={Color.Success_800} fontWeight={FontWeight.Bold}>
                {discount.label}
              </Text>
              <Text color={Color.Success_800} fontWeight={FontWeight.Bold}>
                -{discount.total}
              </Text>
            </Box>
          )}

          <Divider />

          {surchargeTotal && (
            <Box
              display={Display.Flex}
              justifyContent={JustifyContent.SpaceBetween}
              data-no-code="orderSummarySurchargesLine"
            >
              <Text>Surcharges and/or savings</Text>
              <Text data-no-code="orderSummarySurchargesAmount">{surchargeTotal}</Text>
            </Box>
          )}

          {productTotal && (
            <Box display={Display.Flex} justifyContent={JustifyContent.SpaceBetween}>
              <Text>Sides and market items</Text>
              <Text>{productTotal}</Text>
            </Box>
          )}

          <Box
            display={Display.Flex}
            justifyContent={JustifyContent.SpaceBetween}
            data-no-code="orderSummaryDeliveryLine"
          >
            <Text>Delivery</Text>
            <Text data-no-code="orderSummaryDeliveryAmount">{deliveryTotal}</Text>
          </Box>
        </Box>
      ) : (
        <InfoMessage text="The price of your box will show once you’ve added 2–5 recipes." />
      )}

      <Box paddingBottom={0} paddingV={2}>
        <Divider />
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={3}>
          <Box
            display={Display.Flex}
            justifyContent={JustifyContent.SpaceBetween}
            paddingV={2}
            paddingBottom={0}
            data-no-code="orderSummaryTotalLine"
          >
            <Text fontWeight={FontWeight.Bold}>Total</Text>
            {shouldShowOrderSummary ? (
              <Box display={Display.Flex} gap={2}>
                {discount && originalTotal && (
                  <Text
                    data-no-code="orderSummaryOriginalTotal"
                    style={{ textDecoration: 'line-through' }}
                  >
                    {originalTotal}
                  </Text>
                )}
                {discount ? (
                  <Text
                    data-no-code="orderSummaryFinalTotal"
                    fontWeight={FontWeight.Bold}
                    color={Color.Success_800}
                  >
                    {finalTotal}
                  </Text>
                ) : (
                  <Text data-no-code="orderSummaryFinalTotal" fontWeight={FontWeight.Bold}>
                    {finalTotal}
                  </Text>
                )}
              </Box>
            ) : (
              <Text data-no-code="orderSummaryFinalTotal" fontWeight={FontWeight.Bold}>
                £---
              </Text>
            )}
          </Box>
          {isValueBannerEnabled && shouldShowOrderSummary && (
            <ValueBanner averageCostPerPortion={deliveryPricing.averageCostPerPortion} />
          )}
        </Box>
      </Box>
    </Box>
  )
}
